/*
  Carnevore Mountain cart drawer additions, loaded by snippets/cart-drawer.liquid.

  These elements are rendered inside .drawer__inner, which is repainted from the Section Rendering API
  after every cart change. Each repaint creates them again, so each one carries what it needs across
  the repaint itself:
  - cm-cart-delivery: the free delivery bar. Animates the fill from the previous bar's value.
  - cm-cart-upsells: product suggestions at the end of the item list. Loads them only once the drawer
    is open, reuses them across repaints, and adds a product through CartDrawer.addItems
    (assets/cart-drawer.js), which keeps the drawer open.
  - cm-variant-select: the variant dropdown on a cart line. Hands the change to CartDrawer.swapVariant.
*/

class CartDeliveryBar extends HTMLElement {
  // Fill (0 to 1) of the bar that was on the page before this one; null until the first bar connects.
  static lastProgress = null;

  connectedCallback() {
    const progress = parseFloat(this.dataset.progress) || 0;
    const from = CartDeliveryBar.lastProgress;
    // An empty cart renders a hidden bar, so the next add fills it from zero.
    CartDeliveryBar.lastProgress = this.hidden ? 0 : progress;
    if (this.hidden || from === null || from === progress) return;

    const fill = this.querySelector('.cm-cart-delivery__fill');
    if (!fill) return;
    // Paint the previous value without a transition, then let the transition run to the new one.
    fill.style.transition = 'none';
    fill.style.setProperty('--cm-progress', from);
    getComputedStyle(fill).transform;
    fill.style.removeProperty('transition');
    fill.style.setProperty('--cm-progress', progress);
    if (from < 1 && progress >= 1) this.classList.add('is-celebrating');
  }
}

customElements.define('cm-cart-delivery', CartDeliveryBar);

class CartUpsells extends HTMLElement {
  // Suggestion pools, by first product and cart products: [{ id, html }]. Repaints reuse them.
  static pools = new Map();
  // Requests in flight, by the same key, so two repaints never ask twice.
  static requests = new Map();
  // The pool shown last. A repaint paints it straight away, while a pool for the new cart loads.
  static lastPool = [];

  constructor() {
    super();
    this.addEventListener('click', this.onClick.bind(this));
  }

  connectedCallback() {
    this.drawer = this.closest('cart-drawer');
    this.list = this.querySelector('.cm-cart-upsells__list');
    this.limit = parseInt(this.dataset.limit, 10) || 3;
    this.exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    this.key = `${this.dataset.productId}|${this.dataset.exclude}`;

    this.render(CartUpsells.pools.get(this.key) || CartUpsells.lastPool);
    if (this.drawer?.classList.contains('active')) {
      this.load();
    } else {
      // Nothing is requested before the drawer opens, so suggestions never slow the page or the drawer.
      this.onDrawerOpen = () => this.load();
      this.drawer?.addEventListener('cart-drawer:open', this.onDrawerOpen, { once: true });
    }
  }

  disconnectedCallback() {
    if (this.onDrawerOpen) this.drawer?.removeEventListener('cart-drawer:open', this.onDrawerOpen);
  }

  load() {
    const key = this.key;
    if (CartUpsells.pools.has(key)) return this.show(CartUpsells.pools.get(key));
    if (!CartUpsells.requests.has(key)) {
      const request = this.fetchPool()
        .then((pool) => {
          CartUpsells.pools.set(key, pool);
          return pool;
        })
        .catch((error) => {
          console.error(error);
          return null;
        })
        .finally(() => CartUpsells.requests.delete(key));
      CartUpsells.requests.set(key, request);
    }
    CartUpsells.requests.get(key).then((pool) => {
      if (pool && this.isConnected) this.show(pool);
    });
  }

  show(pool) {
    CartUpsells.lastPool = pool;
    this.render(pool);
  }

  /**
   * Builds the suggestion pool for the first product in the cart: complementary products, then related
   * products when there are too few, then the fallback collection. Each response already leaves out
   * products in the cart and sold-out products (sections/cm-cart-upsells.liquid).
   * @returns {Promise<Array<{id: string, html: string}>>}
   */
  async fetchPool() {
    const pool = [];
    const seen = new Set();
    let fallback = [];
    const take = (card) => {
      if (seen.has(card.dataset.productId)) return;
      seen.add(card.dataset.productId);
      pool.push({ id: card.dataset.productId, html: card.outerHTML });
    };

    for (const intent of ['complementary', 'related']) {
      const doc = await this.fetchSection(intent);
      doc.querySelectorAll('[data-cm-upsell][data-source="recommendation"]').forEach(take);
      fallback = doc.querySelectorAll('[data-cm-upsell][data-source="collection"]');
      if (pool.length >= this.limit) break;
    }
    fallback.forEach(take);
    return pool;
  }

  fetchSection(intent) {
    const params = new URLSearchParams({
      product_id: this.dataset.productId,
      limit: Math.min(10, this.limit + 4),
      intent,
      section_id: this.dataset.section,
    });
    return fetch(`${this.dataset.url}?${params}`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }

  /**
   * Shows the first cards of a pool that are not in the cart. Leaves the list alone when the same cards
   * are already showing, so focus on a "+ Add" button survives a pool that loads after a repaint.
   * @param {Array<{id: string, html: string}>} pool
   */
  render(pool) {
    const cards = pool.filter((card) => !this.exclude.has(card.id)).slice(0, this.limit);
    const ids = cards.map((card) => card.id).join(',');
    if (ids === this.dataset.shown) return;

    const buttons = [...this.list.querySelectorAll('[data-cm-upsell-add]')];
    const focusIndex = buttons.indexOf(document.activeElement);
    this.list.innerHTML = cards.map((card) => card.html).join('');
    this.dataset.shown = ids;
    this.hidden = cards.length === 0;
    if (focusIndex >= 0) this.focusButton(focusIndex);
  }

  // Focuses the "+ Add" button at this position, or the last one; returns it (null when there is none).
  // The suggestions sit between the first and last stops of Dawn's focus trap, so the trap needs no reset.
  focusButton(index) {
    const buttons = this.hidden ? [] : [...this.list.querySelectorAll('[data-cm-upsell-add]')];
    const button = buttons[Math.min(index, buttons.length - 1)] || null;
    if (button) button.focus();
    return button;
  }

  onClick(event) {
    const button = event.target.closest('[data-cm-upsell-add]');
    if (!button || !this.drawer?.addItems || button.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();

    const index = [...this.list.querySelectorAll('[data-cm-upsell-add]')].indexOf(button);
    const request = this.drawer.addItems(
      [{ id: Number(button.dataset.variantId), quantity: Number(button.dataset.quantity) || 1 }],
      {
        message: `Added ${button.dataset.title} to your cart.`,
        // Keep focus where it was: on the "+ Add" button that takes this one's place.
        focus: () => this.drawer.querySelector('cm-cart-upsells')?.focusButton(index),
      }
    );
    if (!request) return; // the drawer is busy with another change

    this.querySelector('.cm-cart-upsells__error').textContent = '';
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    request.catch((error) => {
      // Not added (sold out since the suggestions loaded, for example): stop suggesting it, and say why.
      if (!this.isConnected) return;
      button.removeAttribute('aria-disabled');
      button.classList.remove('is-loading');
      const productId = button.closest('[data-cm-upsell]').dataset.productId;
      CartUpsells.pools.forEach((pool, key) => CartUpsells.pools.set(key, pool.filter((card) => card.id !== productId)));
      CartUpsells.lastPool = CartUpsells.lastPool.filter((card) => card.id !== productId);
      this.render(CartUpsells.pools.get(this.key) || CartUpsells.lastPool);
      this.hidden = false; // keep the message in view even if no suggestion is left
      this.querySelector('.cm-cart-upsells__error').textContent = error.message;
    });
  }
}

customElements.define('cm-cart-upsells', CartUpsells);

class CartVariantSelect extends HTMLElement {
  connectedCallback() {
    if (this.select) return;
    this.select = this.querySelector('select');
    this.select.addEventListener('change', (event) => {
      // Dawn's cart-items listens for changes to quantity boxes on the whole list; keep this one away from it.
      event.stopPropagation();
      this.onChange();
    });
  }

  onChange() {
    const drawer = this.closest('cart-drawer');
    const from = this.dataset.variantId;
    const to = this.select.value;
    if (to === from) return;
    if (!drawer?.swapVariant) {
      this.select.value = from;
      return;
    }

    const properties = this.querySelector('[data-properties]');
    drawer.swapVariant({
      line: Number(this.dataset.line),
      key: this.dataset.lineKey,
      from: Number(from),
      to: Number(to),
      quantity: Number(this.dataset.quantity),
      properties: properties ? JSON.parse(properties.textContent || '{}') || {} : {},
      sellingPlan: this.dataset.sellingPlan ? Number(this.dataset.sellingPlan) : null,
      message: `${this.dataset.productTitle}: ${this.dataset.optionName} changed to ${this.select.selectedOptions[0].dataset.title}.`,
      select: this.select,
    });
  }
}

customElements.define('cm-variant-select', CartVariantSelect);
